import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Raw node data for lazy hydration — kept as a plain Map (not React state)
 * to avoid overhead for nodes the user hasn't interacted with.
 */
export const rawProcedureNodeMap = new Map<string, any>();

type ProcedureEditState = {
  metadata: any;
  rootChildIds: string[];
  /** Per-node data keyed by _id. Replaces atomFamily. */
  nodes: Record<string, any>;
  isDirty: boolean;
  /** All known node IDs — used for cleanup on unmount. */
  nodeIds: string[];

  setMetadata: (metadata: any) => void;
  updateMetadata: (patch: Partial<any>) => void;
  setRootChildIds: (ids: string[]) => void;
  setNode: (nodeId: string, data: any) => void;
  updateNode: (nodeId: string, patch: Partial<any>) => void;
  setNodes: (nodes: Record<string, any>) => void;
  /**
   * Replace root order and section child lists in one update, so no render
   * ever sees a node in two containers (or in none) mid-move.
   */
  setLayout: (
    rootChildIds: string[],
    sectionChildIds: Record<string, string[]>,
  ) => void;
  setIsDirty: (dirty: boolean) => void;
  setNodeIds: (ids: string[]) => void;
  /** Batch initialize all state from parsed procedure GraphQL data. */
  initialize: (opts: {
    metadata: any;
    nodes: Record<string, any>;
    rootChildIds: string[];
    nodeIds: string[];
  }) => void;
  /** Reset all state on unmount. */
  reset: () => void;
};

const INITIAL_STATE = {
  metadata: {
    _id: "",
    name: "",
    description: "",
    signatures: [],
    allowUsersToAddMoreSignatures: false,
    pageHeader: null,
    pageFooter: null,
  },
  rootChildIds: [] as string[],
  nodes: {} as Record<string, any>,
  isDirty: false,
  nodeIds: [] as string[],
};

export const useProcedureEditStore = create<ProcedureEditState>()(
  devtools(
    (set) => ({
      ...INITIAL_STATE,

      setMetadata: (metadata) => set({ metadata }),
      updateMetadata: (patch) =>
        set((s) => ({ metadata: { ...s.metadata, ...patch } })),
      setRootChildIds: (rootChildIds) => set({ rootChildIds }),
      setNode: (nodeId, data) =>
        set((s) => ({ nodes: { ...s.nodes, [nodeId]: data } })),
      updateNode: (nodeId, patch) =>
        set((s) => ({
          nodes: {
            ...s.nodes,
            [nodeId]: s.nodes[nodeId]
              ? { ...s.nodes[nodeId], ...patch }
              : patch,
          },
        })),
      setNodes: (nodes) => set({ nodes }),
      setLayout: (rootChildIds, sectionChildIds) =>
        set((s) => {
          const nodes = { ...s.nodes };
          for (const [sectionId, childIds] of Object.entries(sectionChildIds)) {
            if (nodes[sectionId]) {
              nodes[sectionId] = { ...nodes[sectionId], childIds };
            }
          }
          return { rootChildIds, nodes, isDirty: true };
        }),
      setIsDirty: (isDirty) => set({ isDirty }),
      setNodeIds: (nodeIds) => set({ nodeIds }),

      initialize: ({ metadata, nodes, rootChildIds, nodeIds }) =>
        set({ metadata, nodes, rootChildIds, nodeIds, isDirty: false }),

      reset: () => set({ ...INITIAL_STATE }),
    }),
    { name: "ProcedureEditStore" },
  ),
);
